// Canonical Cold Booting content definitions.
//
// Single source of truth for the module's homeworlds and legacies, shared by:
//   - scripts/seedColdBootingModule.ts  (writes the `properties` docs to Firestore)
//   - data/campaignModules.ts           (module registry / wizard defaults)
//   - content generation for blueprint-summary cards + legacy rulebook pages
//
// Keep this file free of Nuxt/Firebase imports so it can run under plain Node.

export const COLD_BOOTING_TAG = 'cold-booting';
export const SOL_STANDARD_TAG = 'sol-standard';

export interface ColdHomeworldDef {
	id: string;
	name: string;
	tag: string; // short code, matches the existing `tag` field convention
	description: string;
	strengths: string[];
	weaknesses: string[];
	stats: { label: string; value: string }[];
	effectIds?: string[]; // effect ids to attach as effectRefs (mechanical grants)
}

export interface ColdLegacyDef {
	id: string;
	name: string;
	tag: string;
	description: string;
	boostedSkills: string[]; // choose one
	legacyItem: string;
	knack: { name: string; text: string };
	quirks: string[];
}

// ---------------------------------------------------------------------------
// Homeworlds — the Oblivion's Edge / Outer Rim sector. Players in Cold Booting
// start in Terminus Major but can hail from anywhere in this sector.
// ---------------------------------------------------------------------------
export const COLD_HOMEWORLDS: ColdHomeworldDef[] = [
	{
		id: 'homeworld-terminus-major',
		name: 'Terminus Major',
		tag: 'HW-TerminusMajor',
		description:
			'Raised in the tunnel-towns at the base of the crag city, breathing land-train exhaust and reading the water ticker before the news.',
		strengths: [
			'Water-thrift; you make a ration stretch where others go dry',
			'Crowd-hardy; at home in press, smog and noise',
		],
		weaknesses: [
			'Soft in open country; the desert is a rumour you grew up beside, not in',
			'A tunnel-town chip on the shoulder toward anyone who lives higher up the crag',
		],
		stats: [
			{ label: 'Gravity', value: '0.9 g, close enough to standard' },
			{ label: 'Atmosphere', value: 'Cold desert world, thin and dry; the city recycles its own' },
			{ label: 'Population', value: 'About a million, give or take the ones nobody counts' },
			{ label: 'Principal Exports', value: 'Water haulage, salvage, cheap desperate labour' },
		],
	},
	{
		id: 'homeworld-terminus-station',
		name: 'Terminus Station',
		tag: 'HW-TerminusStation',
		description:
			'Born on the orbital ring over Terminus, close enough to read the crag as a smudge of light, too poor to ever afford the lift down to it.',
		strengths: [
			'Zero- and low-g ease; corridors, gantries and handholds are second nature',
			'Sharp with a sealed suit and a pressure alarm',
		],
		weaknesses: [
			'Full-g and open sky wear you down fast',
			'Station-bred squeamishness about dirt, weather and wild air',
		],
		stats: [
			{ label: 'Gravity', value: 'Spin gravity, 0.4 g at the rim' },
			{ label: 'Atmosphere', value: 'Sealed and metered; every breath is billed' },
			{ label: 'Population', value: 'Ring crews, dockers, and Corp permit-holders' },
			{ label: 'Principal Exports', value: 'Cargo handling, orbital services, off-world monopoly' },
		],
	},
	{
		id: 'homeworld-fracture',
		name: 'Fracture',
		tag: 'HW-Fracture',
		description:
			'A rugged colony world of tectonic instability and fractured continents, rich in minerals and poor in law. The smallest planet or the biggest asteroid in the Iron Line, depending who you ask.',
		strengths: [
			'Sure-footed on shifting, broken ground',
			'Self-reliant; you fix it yourself or you go without',
		],
		weaknesses: [
			'No trust reflex; hard to lean on a crew you have just met',
			'Poor comms discipline; where you grew up, nobody was listening anyway',
		],
		stats: [
			{ label: 'Gravity', value: '1.1 g and never quite still' },
			{ label: 'Atmosphere', value: 'Breathable, dusty, quake-cracked' },
			{ label: 'Population', value: 'Scattered claim-holders and wildcat mining camps' },
			{ label: 'Principal Exports', value: 'Rare minerals, contraband, feuds' },
		],
	},
	{
		id: 'homeworld-blackreach',
		effectIds: ['light-90deg-4bright-8dim-white'],
		name: 'Blackreach System',
		tag: 'HW-Blackreach',
		description:
			'A mining system whose worlds have been stripped for centuries. You were raised deep in the shafts, where the dark is a room you know by touch.',
		strengths: [
			'Dark-adapted; you read tunnels and low light by feel',
			'Unbothered by tight, deep, enclosed places',
		],
		weaknesses: [
			'Sun-blind; open glare and hard daylight punish you',
			'Restless in wide-open space with no walls to reckon against',
		],
		stats: [
			{ label: 'Gravity', value: 'Varies by world; you grew up under 0.8 g' },
			{ label: 'Atmosphere', value: 'Pumped and filtered; the real air ran out generations ago' },
			{ label: 'Population', value: 'Shaft crews working played-out seams' },
			{ label: 'Principal Exports', value: 'Ore tailings, deep-drilling expertise' },
		],
	},
	{
		id: 'homeworld-vultures-gate',
		name: "Vulture's Gate",
		tag: 'HW-VulturesGate',
		description:
			'Home of the scavenger clans and the black-market shipbreakers. You were raised cutting dead ships apart for the living metal in them.',
		strengths: [
			'Salvage instinct; you see the worth and the danger in a wreck at a glance',
			'Hazard-calm; torch-fire, sharp edges and vacuum do not rush you',
		],
		weaknesses: [
			'Clan-tribal; outsiders are marks or rivals until proven otherwise',
			'Distrusted dirtside, where "breaker" is half an insult',
		],
		stats: [
			{ label: 'Gravity', value: 'Micro-g yards and tethered hulks' },
			{ label: 'Atmosphere', value: 'Suit air and patched pressure tents' },
			{ label: 'Population', value: 'Shipbreaking clans and wreck-recovery crews' },
			{ label: 'Principal Exports', value: 'Reclaimed hull metal, salvaged parts, no questions' },
		],
	},
	{
		id: 'homeworld-iron-line',
		name: 'The Iron Line',
		tag: 'HW-IronLine',
		description:
			'The great iron-rich asteroid graveyard, billions of kilometres of easily-mined ore. You were belt-born, raised weightless among the rocks.',
		strengths: [
			'Zero-g mastery; you move and work in freefall like walking',
			'Precise hands trained on delicate, drifting work',
		],
		weaknesses: [
			'Full-g endurance suffers; heavy worlds feel like wearing an anchor',
			'Bone and muscle built for the void, not the ground',
		],
		stats: [
			{ label: 'Gravity', value: 'Effectively none' },
			{ label: 'Atmosphere', value: 'Bottled; the belt has none of its own' },
			{ label: 'Population', value: 'Low-g miners and ore-freighter crews' },
			{ label: 'Principal Exports', value: 'Metallic ore, refined feedstock for the Core' },
		],
	},
	{
		id: 'homeworld-rust-halo',
		name: 'Rust Halo',
		tag: 'HW-RustHalo',
		description:
			'A sector of aging orbital shipyards, refineries and half-abandoned infrastructure kept alive by constant improvisation. You grew up patching things older than your grandparents.',
		strengths: [
			'Jury-rig anything; you have never met a machine you could not coax along',
			'Comfortable in derelict, half-powered, failing structures',
		],
		weaknesses: [
			'Refinery lungs; harsh air and exertion cost you sooner',
			'Make-do habits that horrify anyone who wants it done to spec',
		],
		stats: [
			{ label: 'Gravity', value: 'Assorted spin habitats, 0.3-0.7 g' },
			{ label: 'Atmosphere', value: 'Recycled and faintly toxic; the filters are also ancient' },
			{ label: 'Population', value: 'Refinery hands, yard dogs, and squatters' },
			{ label: 'Principal Exports', value: 'Refit parts, refined metals, rust' },
		],
	},
	{
		id: 'homeworld-drift',
		name: 'Drift',
		tag: 'HW-Drift',
		description:
			'A massive independent free-port at the crossing of several trade corridors, home to traders, smugglers, bounty hunters and anyone who prefers no planetary law. You were raised in the crowd.',
		strengths: [
			'Streetwise; you read a room, a mark and a lie fast',
			'Many tongues and many manners; you fit in anywhere for a while',
		],
		weaknesses: [
			'No roots; nowhere is really home and everyone is a little bit a mark',
			'Trouble trusting a straight deal when you have only known angles',
		],
		stats: [
			{ label: 'Gravity', value: 'Spin gravity, 0.6 g on the promenade' },
			{ label: 'Atmosphere', value: 'Sealed, crowded, and always slightly too warm' },
			{ label: 'Population', value: 'Everyone passing through, and the ones who never left' },
			{ label: 'Principal Exports', value: 'Trade, contraband, information, discretion' },
		],
	},
	{
		id: 'homeworld-last-light',
		name: 'Last Light',
		tag: 'HW-LastLight',
		description:
			'The furthest major population centre still counted as civilised space; its beacon arrays mark the edge of the Core for everyone heading out. You are a child of the frontier edge.',
		strengths: [
			'Worldly and well-connected; you know a bit of everything and everyone',
			'Frontier poise; the edge of the map does not frighten you',
		],
		weaknesses: [
			'Softer than the deep-Rim born; you had comforts they never did',
			'Core-dependent habits that fail you past the beacons',
		],
		stats: [
			{ label: 'Gravity', value: '1.0 g, comfortably standard' },
			{ label: 'Atmosphere', value: 'Breathable open air; a genuine sky' },
			{ label: 'Population', value: 'The last real city before the frontier' },
			{ label: 'Principal Exports', value: 'Outfitting, navigation, last-chance supplies' },
		],
	},
	{
		id: 'homeworld-ship-born',
		name: 'Ship-Born (No Fixed World)',
		tag: 'HW-ShipBorn',
		description:
			'You were born and raised aboard hauler crews working the Iron Run and the Dust Road; the corridors of a ship are the only home world you have ever had.',
		strengths: [
			'At home aboard any vessel; you know decks, watches and cramped living',
			'Rootless adaptability; you have seen a dozen ports and belonged to none',
		],
		weaknesses: [
			'Planetsick; open ground, weather and horizon unsettle you',
			'No home to fall back on, and no family plot to defend',
		],
		stats: [
			{ label: 'Gravity', value: 'Whatever the drive was pulling that week' },
			{ label: 'Atmosphere', value: 'Recycled ship air; you can smell a bad scrubber' },
			{ label: 'Population', value: 'One crew, then the next' },
			{ label: 'Principal Exports', value: 'Whatever was in the hold' },
		],
	},
];

// ---------------------------------------------------------------------------
// Legacies — five backgrounds specific to the Cold Booting setting.
// ---------------------------------------------------------------------------
export const COLD_LEGACIES: ColdLegacyDef[] = [
	{
		id: 'legacy-tunnel-town-kid-legacy',
		name: 'Tunnel-Town Kid',
		tag: 'TunnelTownKid',
		description:
			'You were raised at the base of the crag under the water leash, where a household’s standing is its water tier and a debt is measured in days of supply.',
		boostedSkills: ['Intel - Survival', 'Intel - Street Smarts'],
		legacyItem:
			'A jury-rigged personal water-still, ugly, patched, and yours; it wrings a mouthful of clean water out of almost anything given time.',
		knack: {
			name: 'Four Days from Thirsty',
			text:
				'You go markedly longer than others before dehydration bites, and you read the desperation in a room, a queue or a haggle the way others read a clock.',
		},
		quirks: [
			'You never leave a tap running, ever.',
			'A dripping tap drives you up the wall; you will cross a room to stop it.',
		],
	},
	{
		id: 'legacy-board-rat-legacy',
		name: 'Board Rat',
		tag: 'BoardRat',
		description:
			'You grew up working the spot-price auctions, sniping anonymous lots off the board and learning the trade floor before you learned your letters.',
		boostedSkills: ['Intel - Marketeering', 'Espionage - Social'],
		legacyItem:
			'A cracked handheld spot-board reader, screen half dead, that still pulls live prices a beat before the public feed.',
		knack: {
			name: 'Yonk',
			text:
				'Once per session you can read or ride a price swing on the spot board, calling roughly which way a lot will clear; you may reroll a failed haggling or Marketeering check.',
		},
		quirks: [
			'You price everything on sight, including your friends.',
			'You cannot walk past an auction without stopping to watch it clear.',
		],
	},
	{
		id: 'legacy-rig-rat-legacy',
		name: 'Rig Rat',
		tag: 'RigRat',
		description:
			'You were raised in the crawlspaces and engine-guts of a land-train, small and wiry, handed a wrench before you could reach the pedals.',
		boostedSkills: ['Vehicles - Piloting (Land/Sea)', 'Engineering - Repair'],
		legacyItem:
			'A worn personal multi-tool that somehow fits every fastener on every rig you have ever met; you would sooner lose a finger than lose it.',
		knack: {
			name: 'Knows Every Bolt',
			text:
				'You have advantage when locating a fault in a vehicle or squeezing through its access spaces, ducts and crawlways.',
		},
		quirks: [
			'You sleep better to the hum of a running engine.',
			'You distrust anything that does not rattle a little.',
		],
	},
	{
		id: 'legacy-breaker-legacy',
		name: 'Breaker',
		tag: 'Breaker',
		description:
			"You came up in a Vulture's Gate shipbreaking clan, cutting dead hulls apart for the living metal in them; you know exactly where a structure wants to fail.",
		boostedSkills: ['Engineering - Rigging', 'Engineering - Demolitions'],
		legacyItem:
			'A clan cutting-torch and rigger’s harness, scarred with old cut-lines and somebody else’s clan marks under yours.',
		knack: {
			name: 'Cut Line',
			text:
				'You can read where a structure, hull or wreck will give way, gaining an edge when salvaging, breaching or bringing something down in one piece (or many).',
		},
		quirks: [
			'You value everything by its scrap weight first, its use second.',
			'You keep your clan cut-marks even when it would be smarter not to.',
		],
	},
	{
		id: 'legacy-reaver-wise-legacy',
		name: 'Reaver-Wise',
		tag: 'ReaverWise',
		description:
			'The desert tried to finish you and did not. You have survived the open sand and the things that hunt beneath it, and you carry the lesson in your bones.',
		boostedSkills: ['Intel - Survival', 'Intel - Aliens'],
		legacyItem:
			'A reaver-tooth blade and a scent-fouling sand-cloak; the tooth cuts, the cloak keeps the deep sand from tasting you.',
		knack: {
			name: 'Reaver Sense',
			text:
				'You feel the ground-tremor of a burrowing predator before it strikes, gaining advantage on awareness against ambushes in open sand and a chance to avoid drawing the deep hunters at all.',
		},
		quirks: [
			'You will not stand still on open sand; you keep moving, always.',
			'You drink alone, back to a wall, watching the horizon.',
		],
	},
];

export const COLD_HOMEWORLD_IDS = COLD_HOMEWORLDS.map((h) => h.id);
export const COLD_LEGACY_IDS = COLD_LEGACIES.map((l) => l.id);
